import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'

interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  className?: string
  overlayClassName?: string
  drawerClassName?: string
}

export const Drawer = ({
  isOpen,
  onClose,
  children,
  className = '',
  overlayClassName = '',
  drawerClassName = '',
}: DrawerProps) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const previousActiveElementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (isOpen) {
      previousActiveElementRef.current =
        document.activeElement as HTMLElement | null

      const handleKeyDown = (e: globalThis.KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.stopPropagation()
          onClose()
          return
        }

        if (e.key === 'Tab') {
          const drawerNode = contentRef.current
          if (!drawerNode) return

          const focusableSelectors = [
            'a[href]',
            'button:not([disabled])',
            'textarea:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
          ].join(',')

          const focusableElements = Array.from(
            drawerNode.querySelectorAll<HTMLElement>(focusableSelectors)
          ).filter(
            (el) =>
              !el.hasAttribute('disabled') &&
              el.getAttribute('aria-hidden') !== 'true'
          )

          if (focusableElements.length === 0) {
            e.preventDefault()
            return
          }

          const firstEl = focusableElements[0]
          const lastEl = focusableElements[focusableElements.length - 1]
          const activeEl = document.activeElement as HTMLElement

          if (e.shiftKey && activeEl === firstEl) {
            e.preventDefault()
            lastEl.focus()
            return
          }
          if (!e.shiftKey && activeEl === lastEl) {
            e.preventDefault()
            firstEl.focus()
            return
          }
        }
      }

      const handleClickOutside = (e: globalThis.MouseEvent) => {
        if (
          contentRef.current &&
          !contentRef.current.contains(e.target as Node)
        ) {
          onClose()
        }
      }

      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleKeyDown)
      document.addEventListener('mousedown', handleClickOutside)

      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.setAttribute('tabIndex', '-1')
          contentRef.current.focus()
        }
      }, 0)

      return () => {
        document.body.style.overflow = ''
        document.removeEventListener('keydown', handleKeyDown)
        document.removeEventListener('mousedown', handleClickOutside)

        if (previousActiveElementRef.current) {
          previousActiveElementRef.current.focus()
        }
      }
    } else {
      previousActiveElementRef.current = null
    }
  }, [isOpen, onClose])

  const mainElement =
    typeof window !== 'undefined' ? document.querySelector('main') : null
  const portalContainer = mainElement || document.body

  return ReactDOM.createPortal(
    <div
      className={`tv-guide-drawer ${className} ${isOpen ? 'open' : 'close'}`}
    >
      <div className={`drawer-overlay ${overlayClassName}`} onClick={onClose} />
      <div
        ref={contentRef}
        className={`drawer-content ${drawerClassName}`}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>,
    portalContainer
  )
}
